import React, { useEffect, useRef, useState } from 'react';
import { Tooltip, Typography } from '@mui/material';
import Badge from '../Badge/Badge';

export function RowIndexCell({ page = 0, pageSize = 20, rowIndex = 0 }) {
  const serial = page * pageSize + rowIndex + 1;
  return (
    <Typography component="span" variant="body2" color="text.secondary">
      {serial}
    </Typography>
  );
}

export function TextCell({
  value,
  maxWidth = 240,
  maxCharacters,
  fontWeight,
  align = 'left',
  muted = false,
}) {
  const textRef = useRef(null);
  const [showTooltip, setShowTooltip] = useState(false);
  const displayValue = value ?? '-';
  const characters = Array.from(String(displayValue));
  const visibleValue = maxCharacters && characters.length > maxCharacters
    ? `${characters.slice(0, maxCharacters).join('')}...`
    : displayValue;

  useEffect(() => {
    const element = textRef.current;
    if (element) {
      setShowTooltip(visibleValue !== displayValue || element.scrollWidth > element.clientWidth);
    }
  }, [displayValue, visibleValue]);

  return (
    <Tooltip title={showTooltip ? displayValue : ''} placement="top" arrow>
      <Typography
        ref={textRef}
        noWrap
        variant="body2"
        color={muted ? 'text.secondary' : 'text.primary'}
        sx={{
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          maxWidth,
          width: maxWidth === '100%' ? '100%' : undefined,
          fontWeight,
          textAlign: align,
          display: maxWidth === '100%' ? 'block' : 'inline-block',
          verticalAlign: 'middle',
        }}
      >
        {visibleValue}
      </Typography>
    </Tooltip>
  );
}

const BADGE_VARIANT_MAP = {
  success: 'success',
  warning: 'warning',
  danger: 'danger',
  info: 'info',
  neutral: 'neutral',
  primary: 'primary',
  default: 'default',
};

export function BadgeCell({ label, variant = 'neutral', size = 'sm' }) {
  const resolvedVariant = BADGE_VARIANT_MAP[variant] || 'neutral';
  if (!label) {
    return null;
  }
  return (
    <Badge variant={resolvedVariant} size={size}>
      {label}
    </Badge>
  );
}

export function mapBooleanToBadgeVariant(isTruthy, {
  trueVariant = 'success',
  falseVariant = 'neutral',
  trueLabel = 'Yes',
  falseLabel = 'No',
} = {}) {
  return {
    label: isTruthy ? trueLabel : falseLabel,
    variant: isTruthy ? trueVariant : falseVariant,
  };
}
